import PdfTableViewer from "./pdfViewer/PdfTableViewer";
import { useTheme } from "@mui/material/styles";
import { useState, useEffect, useCallback } from "react";
import { extractPdfData } from "services/aiAgents";

const PdfExtractionFlow = ({
  fileId,
  filename,
  fileDownloadUrl,
  onApprove,
  onCancel,
  onTableViewerReady = null,
  extractionResult: providedExtractionResult = null,
  skipExtraction = false,
}) => {
  const theme = useTheme();
  const [extractionResult, setExtractionResult] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [showTableViewer, setShowTableViewer] = useState(false);

  const startExtraction = useCallback(async () => {
    setIsExtracting(true);
    setError(null);

    try {
      const result = await extractPdfData(fileDownloadUrl, {
        generateExcel: true,
        generatePdf: false,
        cleanTable: true,
        removeSubtotal: true,
      });

      if (result.success && result.data) {
        setExtractionResult(result);
        setShowTableViewer(true);
        // Notify parent that table viewer is ready to show
        if (onTableViewerReady) {
          onTableViewerReady();
        }
      } else {
        throw new Error(result.error || "PDF extraction failed");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsExtracting(false);
    }
  }, [fileDownloadUrl, onTableViewerReady]);

  // Initialize extraction result or start extraction when component mounts
  useEffect(() => {
    if (skipExtraction && providedExtractionResult) {
      // Use provided extraction result
      setExtractionResult(providedExtractionResult);
      setShowTableViewer(true);
      if (onTableViewerReady) {
        onTableViewerReady();
      }
    } else if (fileDownloadUrl && !extractionResult && !isExtracting) {
      // Start extraction
      startExtraction();
    }
  }, [
    fileDownloadUrl,
    extractionResult,
    isExtracting,
    startExtraction,
    skipExtraction,
    providedExtractionResult,
    onTableViewerReady,
  ]);

  const handleApprove = (analysisResult) => {
    // The PDF viewer now handles the CSV conversion and calls claims AI directly
    // We just need to pass the analysis result to the parent component
    if (analysisResult) {
      // Pass the analysis result directly - it's already in the correct format from claims AI
      onApprove(analysisResult);
    }
  };

  // Error state
  if (error) {
    return (
      <div
        style={{
          padding: "24px",
          backgroundColor: "#fafafa",
          border: `1px solid ${theme.brand.gray.gainsboro}`,
          borderRadius: "8px",
          textAlign: "center",
        }}
      >
        <h3 style={{ color: "#333", margin: "0 0 12px 0", fontWeight: "600" }}>
          {"PDF Extraction Failed"}
        </h3>
        <p style={{ color: "#666", margin: "0 0 24px 0", lineHeight: "1.5" }}>
          {error}
        </p>
        <div>
          <button
            onClick={startExtraction}
            style={{
              backgroundColor: "#1976d2",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "6px",
              cursor: "pointer",
              marginRight: "12px",
              fontWeight: "500",
            }}
          >
            {"Retry"}
          </button>
          <button
            onClick={onCancel}
            style={{
              backgroundColor: "#757575",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            {"Cancel"}
          </button>
        </div>
      </div>
    );
  }

  // Show table viewer if extraction is complete
  if (showTableViewer && extractionResult) {
    return (
      <PdfTableViewer
        fileId={fileId}
        fileDownloadUrl={fileDownloadUrl}
        extractionResult={extractionResult}
        onApprove={handleApprove}
        onCancel={onCancel}
      />
    );
  }

  return null;
};

export default PdfExtractionFlow;
